import HBMessage from "@/components/HBMessage";
import accountLocked from "@/components/LoginModal/shared/accountLocked";
import { getPageInfo, NavigationContext, TrackerLogger } from "@/tracker-v2";
import { isGdMerged } from "@/utils/gaoding";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";

/**
 * App 层级使用的自定义 Hooks 集合
 * 包含 DOM 类管理、错误处理、导航追踪等功能
 */

// ==================== useDomClassManagement ====================

interface UseDomClassManagementProps {
  os: string;
  isMobile: boolean;
  isWeixin: boolean;
  user: any;
  grayTheme?: boolean;
  pathname: string;
}

/**
 * 统一管理 DOM class 操作的自定义 Hook
 * 避免多个 useEffect 分散操作 DOM
 */
export function useDomClassManagement({
  os,
  isMobile,
  isWeixin,
  user,
  grayTheme,
  pathname,
}: UseDomClassManagementProps) {
  // 合并所有 DOM class 操作到一个 useEffect
  useEffect(() => {
    // 1. 设置 OS 和设备相关的 class（只需执行一次）
    document.body.classList.add(`os-${os}`);
    if (isMobile) {
      document.body.classList.add("isMobile");
    }
    if (isWeixin) {
      document.body.classList.add("isWeixin");
    }
    window.GLOBAL_INFO_OS = os;

    // 清理函数（理论上不会触发，因为这些值不会改变）
    return () => {
      document.body.classList.remove(`os-${os}`, "isMobile", "isWeixin");
    };
  }, [os, isMobile, isWeixin]);

  // 2. 稿定模块显隐控制
  useEffect(() => {
    if (isGdMerged(user)) {
      document.body.classList.remove("not-gdmerged");
    } else {
      document.body.classList.add("not-gdmerged");
    }
  }, [user]);

  // 3. 灰色主题控制
  useEffect(() => {
    // 灰色主题的filter会与fixed冲突，导致页面样式混乱
    // https://juejin.cn/post/6844904117974859783
    if (grayTheme && pathname === "/") {
      document.documentElement.classList.add("theme-gray");
    } else {
      document.documentElement.classList.remove("theme-gray");
    }
  }, [grayTheme, pathname]);
}

// ==================== useInitErrorHandler ====================

interface InitError {
  msg?: string;
  status?: number;
  detail?: any;
}

/**
 * 处理初始化错误的自定义 Hook
 * 只在错误第一次出现时处理，避免重复处理
 */
export function useInitErrorHandler(initErr: InitError | null) {
  const hasHandled = useRef(false);

  useEffect(() => {
    // 如果已经处理过或没有错误，直接返回
    if (hasHandled.current || !initErr?.msg) {
      return;
    }

    // 标记为已处理
    hasHandled.current = true;

    // 对账号异常进行特殊处理，需要重置密码
    if (initErr.msg.includes("重新设置账号密码")) {
      accountLocked(initErr);
      return;
    }

    // 其他设备登录
    if (initErr.status === 401 && initErr.detail) {
      import("@/components/LoginModal/otherDeviceLogin").then(
        ({ OtherDeviceLoginInfo }) => {
          OtherDeviceLoginInfo.open(initErr.detail);
        }
      );
      return;
    }

    // 其他错误统一显示消息
    HBMessage.error(initErr.msg);
  }, [initErr]);
}

// ==================== useNavigationTracking ====================

/**
 * 追踪页面导航变化的自定义 Hook
 * 统一处理所有导航相关的事件监听
 */
export function useNavigationTracking() {
  const router = useRouter();

  useEffect(() => {
    // 1. 初始化导航上下文
    NavigationContext.init();

    // 2. 设置初始页面信息
    const pageInfo = getPageInfo();
    NavigationContext.onPageChange(
      window.location.href,
      pageInfo.page_nm,
      pageInfo.sub_page_nm
    );

    TrackerLogger.debug(
      "useNavigationTracking",
      "TrackerV2 已初始化",
      pageInfo
    );

    // 3. 更新 NavigationContext 的通用方法
    const updateNavigationContext = (source: string, url?: string) => {
      const pageInfo = getPageInfo();
      NavigationContext.onPageChange(
        url || window.location.href,
        pageInfo.page_nm,
        pageInfo.sub_page_nm
      );

      TrackerLogger.debug(
        "useNavigationTracking",
        `NavigationContext updated via ${source}`,
        {
          url: url || window.location.href,
          actualPath: window.location.pathname,
          pageInfo,
        }
      );
    };

    // 4. 监听 Next.js 路由变化（最可靠的方式）
    const handleRouteChange = (url: string) => {
      updateNavigationContext("routeChangeComplete", url);
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    // 5. 监听浏览器前进/后退
    const handlePopState = () => {
      updateNavigationContext("popstate");
    };

    window.addEventListener("popstate", handlePopState);

    // 6. 监听 pushState/replaceState（处理使用 history API 的跳转）
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      setTimeout(() => updateNavigationContext("pushState"), 0);
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      setTimeout(() => updateNavigationContext("replaceState"), 0);
    };

    // 7. 清理函数
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      window.removeEventListener("popstate", handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [router.events]);
}
